import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/authStore';
import { Bell, Shield, Users, QrCode, Link2, LogIn, Settings2, Clock, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  auth:        { icon: LogIn,    color: 'bg-violet-50 text-violet-600' },
  team:        { icon: Users,    color: 'bg-blue-50 text-blue-600' },
  role:        { icon: Shield,   color: 'bg-amber-50 text-amber-600' },
  qr:          { icon: QrCode,   color: 'bg-emerald-50 text-emerald-600' },
  integration: { icon: Link2,    color: 'bg-cyan-50 text-cyan-600' },
  submission:  { icon: Settings2, color: 'bg-rose-50 text-rose-600' },
  error:       { icon: AlertTriangle, color: 'bg-red-50 text-red-600' },
};
const fallback = { icon: Clock, color: 'bg-zinc-50 text-zinc-600' };

export const NotificationBell = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const dropRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!token) return;
    const wsBase = API.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/api/ws/notifications/${token}`);

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'audit_event' && msg.entry) {
          setEvents(prev => [msg.entry, ...prev].slice(0, 20));
          setUnread(prev => prev + 1);
        }
        if (msg.type === 'error_alert' && msg.error) {
          const err = msg.error;
          const entry = {
            category: 'error',
            user_name: 'System',
            detail: `${err.source} error: ${err.error_message}`,
            action: 'error_detected',
            timestamp: err.created_at,
            _errorId: err.error_id,
          };
          setEvents(prev => [entry, ...prev].slice(0, 20));
          setUnread(prev => prev + 1);
          toast.error(`New ${err.source} error: ${err.error_message.slice(0, 80)}`, {
            description: `${err.error_type}${err.endpoint ? ` at ${err.endpoint}` : ''}`,
            action: { label: 'View', onClick: () => navigate('/admin') },
            duration: 8000,
          });
        }
      } catch {}
    };

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);

    return () => { clearInterval(ping); ws.close(); };
  }, [token]);

  const handleOpen = () => {
    setOpen(prev => !prev);
    if (!open) setUnread(0);
  };

  const timeAgo = (ts) => {
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropRef} data-testid="notification-bell">
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-full hover:bg-[#F1F5F9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]"
        aria-label={`Notifications${unread > 0 ? ` (${unread} new)` : ''}`}
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5 text-[#475569]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
        {connected && (
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-emerald-500 rounded-full border border-white" data-testid="notification-connected" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-[#E2E8F0] z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Activity</h3>
            {events.length > 0 && (
              <button onClick={() => setEvents([])} className="text-xs text-[#94A3B8] hover:text-[#475569]" data-testid="clear-notifications">
                Clear
              </button>
            )}
          </div>

          {/* Events */}
          <div className="max-h-[320px] overflow-y-auto">
            {events.length === 0 ? (
              <div className="px-4 py-8 text-center" data-testid="no-notifications">
                <Bell className="h-8 w-8 text-[#CBD5E1] mx-auto mb-2" />
                <p className="text-sm text-[#94A3B8]">No recent activity</p>
                <p className="text-xs text-[#CBD5E1] mt-1">Events will appear here in real-time</p>
              </div>
            ) : (
              events.map((e, i) => {
                const meta = CATEGORY_META[e.category] || fallback;
                const Icon = meta.icon;
                return (
                  <div key={`${e.timestamp}-${i}`} className="px-4 py-2.5 hover:bg-[#F8FAFC] border-b border-[#F1F5F9] last:border-0" data-testid={`notification-item-${i}`}>
                    <div className="flex gap-2.5">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${meta.color}`}>
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#1E293B] leading-relaxed">
                          <span className="font-semibold">{e.user_name}</span>{' '}
                          {e.detail || `${e.action} (${e.category})`}
                        </p>
                        <p className="text-[10px] text-[#94A3B8] mt-0.5">{timeAgo(e.timestamp)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <button
            onClick={() => { setOpen(false); navigate('/audit-log'); }}
            className="w-full px-4 py-2.5 text-xs font-medium text-[#1d4ed8] hover:bg-[#EFF6FF] border-t border-[#E2E8F0] flex items-center justify-center gap-1"
            data-testid="view-all-activity"
          >
            View All Activity <ArrowRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
};
